import { useState, useEffect } from 'react'
import { Plus, X, Mail, Figma, FileText, Calendar } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form'
import { Modal } from '@/components/ui/modal'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { api } from '@/services/api'
import { WorkshopTeamLinksField, EMPTY_TEAM_LINK, type WorkshopTeamLinkInput } from '@/components/projects/WorkshopTeamLinksField'
import { fetchClientsWithEmails, formatClientLabel, type ClientWithEmails } from '@/services/clients'
import { PeopleSelect } from '@/components/projects/CreateProjectModal'
import { useInternalPeople } from '@/components/projects/useInternalPeople'

export interface ActivitySector {
  id: number
  nom: string
}

export interface WorkshopFormData {
  clientId?: string
  clientName?: string
  clientEmail?: string
  clientEmails?: string[]
  clientNom?: string
  clientEmailExisting?: string
  date: string
  consultants: string  // Noms affichés (dérivés de consultantIds)
  consultantIds: string[]
  description?: string
  urlsFigma?: string[]
  urlsProposition?: string[]
  prix?: string | number
  activitySectorId?: string
  newActivitySectorName?: string  // For creating new sector
  teamLinks?: WorkshopTeamLinkInput[]
  visibleClient?: boolean
}

export interface CreateWorkshopModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: WorkshopFormData) => void | Promise<void>
  isLoading?: boolean
}

export function CreateWorkshopModal({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: CreateWorkshopModalProps) {
  const [clientType, setClientType] = useState<'existing' | 'new'>('existing')
  const [showNewSectorInput, setShowNewSectorInput] = useState(false)
  const [clients, setClients] = useState<ClientWithEmails[]>([])
  const [activitySectors, setActivitySectors] = useState<ActivitySector[]>([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [loadingSectors, setLoadingSectors] = useState(false)
  const [creatingSector, setCreatingSector] = useState(false)
  const { people, loading: loadingPeople } = useInternalPeople(open)

  const [formData, setFormData] = useState<WorkshopFormData>({
    clientId: '',
    clientName: '',
    clientEmail: '',
    clientEmails: [''],
    clientNom: '',
    clientEmailExisting: '',
    date: '',
    consultants: '',
    consultantIds: [],
    description: '',
    urlsFigma: [''],
    urlsProposition: [''],
    prix: '',
    activitySectorId: '',
    newActivitySectorName: '',
    teamLinks: [{ ...EMPTY_TEAM_LINK }],
    visibleClient: false,
  })

  const toggleConsultant = (id: string) => {
    setFormData((prev) => {
      const next = prev.consultantIds.includes(id)
        ? prev.consultantIds.filter((item) => item !== id)
        : [...prev.consultantIds, id]
      const names = next
        .map((personId) => people.find((person) => person.id === personId)?.name)
        .filter((name): name is string => Boolean(name))
      return { ...prev, consultantIds: next, consultants: names.join(', ') }
    })
  }

  // Fetch clients and activity sectors when modal opens
  useEffect(() => {
    if (open) {
      fetchClients()
      fetchActivitySectors()
    }
  }, [open])

  const fetchClients = async () => {
    setLoadingClients(true)
    try {
      setClients(await fetchClientsWithEmails())
    } catch (error) {
      console.error('Erreur lors de la récupération des clients:', error)
      setClients([])
    } finally {
      setLoadingClients(false)
    }
  }

  const fetchActivitySectors = async () => {
    setLoadingSectors(true)
    try {
      const response = await api.get<{ data: ActivitySector[]; count: number }>('/activity-sectors')
      const sectors = response.data.data || []
      setActivitySectors(sectors)
    } catch (error) {
      console.error('Erreur lors de la récupération des secteurs d\'activité:', error)
      setActivitySectors([])
    } finally {
      setLoadingSectors(false)
    }
  }

  const handleCreateSector = async () => {
    if (!formData.newActivitySectorName?.trim()) {
      alert('Veuillez entrer un nom pour le secteur d\'activité')
      return
    }

    setCreatingSector(true)
    try {
      const response = await api.post<ActivitySector>('/activity-sectors', {
        nom: formData.newActivitySectorName.trim()
      })
      const newSector = response.data
      
      // Add to list and select it
      setActivitySectors(prev => [...prev, newSector])
      setFormData(prev => ({
        ...prev,
        activitySectorId: String(newSector.id),
        newActivitySectorName: ''
      }))
      setShowNewSectorInput(false)
    } catch (error: any) {
      console.error('Erreur lors de la création du secteur:', error)
      const errorMessage = error?.message || error?.response?.data?.detail || 'Erreur lors de la création du secteur'
      alert(errorMessage)
    } finally {
      setCreatingSector(false)
    }
  }

  const handleInputChange = (field: keyof WorkshopFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...(formData.clientEmails || [''])]
    newEmails[index] = value
    setFormData((prev) => ({ ...prev, clientEmails: newEmails }))
  }

  const addEmailField = () => {
    setFormData((prev) => ({
      ...prev,
      clientEmails: [...(prev.clientEmails || ['']), '']
    }))
  }

  const removeEmailField = (index: number) => {
    const newEmails = [...(formData.clientEmails || [''])]
    if (newEmails.length > 1) {
      newEmails.splice(index, 1)
      setFormData((prev) => ({ ...prev, clientEmails: newEmails }))
    }
  }

  // URL management functions
  const handleUrlChange = (urlType: 'urlsFigma' | 'urlsProposition', index: number, value: string) => {
    const newUrls = [...(formData[urlType] || [''])]
    newUrls[index] = value
    setFormData((prev) => ({ ...prev, [urlType]: newUrls }))
  }

  const addUrlField = (urlType: 'urlsFigma' | 'urlsProposition') => {
    setFormData((prev) => ({
      ...prev,
      [urlType]: [...(prev[urlType] || ['']), '']
    }))
  }

  const removeUrlField = (urlType: 'urlsFigma' | 'urlsProposition', index: number) => {
    const newUrls = [...(formData[urlType] || [''])]
    if (newUrls.length > 1) {
      newUrls.splice(index, 1)
      setFormData((prev) => ({ ...prev, [urlType]: newUrls }))
    }
  }

  const handleSubmit = async () => {
    if (formData.consultantIds.length === 0) {
      alert('Sélectionnez au moins un consultant Qolaig')
      return
    }
    // For existing client, use all emails from clientEmails if available
    // For new client, filter out empty emails
    let validEmails: string[] = []
    if (formData.clientId && formData.clientEmails && formData.clientEmails.length > 0) {
      // Existing client - use all stored emails
      validEmails = formData.clientEmails.filter(e => e && e.trim())
    } else {
      // New client - filter out empty emails
      validEmails = (formData.clientEmails || []).filter(e => e && e.trim())
    }
    
    const dataToSubmit = {
      ...formData,
      clientEmails: validEmails.length > 0 ? validEmails : undefined,
      clientEmail: validEmails[0] || formData.clientEmail || ''
    }
    await onSubmit(dataToSubmit)
    // Reset form after successful submission
    resetForm()
  }

  const resetForm = () => {
    setFormData({
      clientId: '',
      clientName: '',
      clientEmail: '',
      clientEmails: [''],
      clientNom: '',
      clientEmailExisting: '',
      date: '',
      consultants: '',
      consultantIds: [],
      description: '',
      urlsFigma: [''],
      urlsProposition: [''],
      prix: '',
      activitySectorId: '',
      newActivitySectorName: '',
      teamLinks: [{ ...EMPTY_TEAM_LINK }],
      visibleClient: false,
    })
    setClientType('existing')
    setShowNewSectorInput(false)
  }

  const handleCancel = () => {
    onOpenChange(false)
    resetForm()
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      hideHeader={true}
      contentClassName="border-4 border-cyan-400 rounded-2xl shadow-2xl"
      footer={
        <div className="flex gap-3 pt-4 justify-start">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isLoading}
            className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
          >
            Créer le workshop
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="bg-white border-b border-gray-200 px-6 py-4 -mx-6 -mt-6 mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Créer un nouveau workshop</h2>
            <p className="text-sm text-gray-500 mt-1">
              Ajoutez un nouveau workshop pour un client existant ou créez un nouveau client
            </p>
          </div>
        </div>

        {/* Client Section */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Client</label>
          <div className="flex gap-3 mb-3">
            <Button
              type="button"
              onClick={() => setClientType('existing')}
              variant="outline"
              className={`flex-1 ${
                clientType === 'existing'
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border-gray-200'
              }`}
            >
              Client existant
            </Button>
            <Button
              type="button"
              onClick={() => setClientType('new')}
              variant="outline"
              className={`flex-1 ${
                clientType === 'new'
                  ? 'bg-gradient-to-r from-cyan-400 to-cyan-500 text-white shadow-md border-transparent hover:from-cyan-500 hover:to-cyan-600'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border-gray-200'
              }`}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau client
            </Button>
          </div>

          {clientType === 'new' ? (
            <div className="space-y-4 border border-blue-200 rounded-lg p-4 bg-blue-50/30">
              <FormField label="Nom du nouveau client" required>
                <Input
                  type="text"
                  value={formData.clientName || ''}
                  onChange={(e) => handleInputChange('clientName', e.target.value)}
                  placeholder="Ex: ACME Corporation"
                  className="bg-white border-gray-200 focus:ring-cyan-400 focus:border-transparent"
                />
              </FormField>
              <FormField label="Emails du client" required>
                <div className="space-y-2">
                  {(formData.clientEmails || ['']).map((email, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => handleEmailChange(index, e.target.value)}
                          placeholder="contact@exemple.com"
                          className="bg-white border-gray-200 focus:ring-cyan-400 focus:border-transparent pl-10"
                        />
                      </div>
                      {(formData.clientEmails?.length || 0) > 1 && (
                        <button
                          type="button"
                          onClick={() => removeEmailField(index)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer cet email"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addEmailField}
                    className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700 mt-2"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter un email
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Chaque email pourra se connecter à la plateforme et accéder au workshop.
                  </p>
                </div>
              </FormField>
            </div>
          ) : (
            <SearchableSelect
              value={formData.clientId || ''}
              onChange={(value) => {
                const selectedClient = clients.find(c => c.id === value)
                if (!selectedClient) return
                const allEmails = selectedClient.emails.length > 0
                  ? selectedClient.emails
                  : (selectedClient.email ? [selectedClient.email] : [])
                setFormData(prev => ({
                  ...prev,
                  clientId: value,
                  clientNom: selectedClient.nom,
                  clientEmailExisting: allEmails[0] || '',
                  clientEmails: allEmails,
                }))
              }}
              options={clients.map(client => ({
                value: client.id,
                label: formatClientLabel(client.nom, client.emails)
              }))}
              placeholder={loadingClients ? "Chargement..." : clients.length === 0 ? "Aucun client disponible" : "Sélectionner un client"}
              searchPlaceholder="Rechercher un client..."
              disabled={loadingClients || clients.length === 0}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
            />
          )}
        </div>

        {/* Consultants Qolaig */}
        <FormField label="Consultants Qolaig" required>
          <PeopleSelect
            people={people}
            selectedIds={formData.consultantIds}
            onToggle={toggleConsultant}
            loading={loadingPeople}
            loadingLabel="Chargement des collaborateurs…"
            placeholder="Sélectionner un ou plusieurs consultants"
            searchPlaceholder="Rechercher un collaborateur..."
            emptyLabel="Aucun collaborateur"
          />
        </FormField>

        {/* Date du workshop */}
        <FormField label="Date du workshop" required>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="date"
              value={formData.date || ''}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent pl-10"
            />
          </div>
        </FormField>

        {/* Description */}
        <FormField label="Description">
          <Textarea
            value={formData.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Décrivez brièvement le workshop"
            rows={4}
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent resize-none"
          />
        </FormField>

        {/* URLs Figma */}
        <FormField label="URLs Figma (optionnel)">
          <div className="space-y-2">
            {(formData.urlsFigma || ['']).map((url, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Figma className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="url"
                    value={url}
                    onChange={(e) => handleUrlChange('urlsFigma', index, e.target.value)}
                    placeholder="https://figma.com/..."
                    className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent pl-10"
                  />
                </div>
                {(formData.urlsFigma?.length || 0) > 1 && (
                  <button
                    type="button"
                    onClick={() => removeUrlField('urlsFigma', index)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => addUrlField('urlsFigma')}
              className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700"
            >
              <Plus className="w-4 h-4" />
              Ajouter une URL Figma
            </button>
          </div>
        </FormField>

        {/* URLs Proposition */}
        <FormField label="URLs Proposition (optionnel)">
          <div className="space-y-2">
            {(formData.urlsProposition || ['']).map((url, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="relative flex-1">
                  <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="url"
                    value={url}
                    onChange={(e) => handleUrlChange('urlsProposition', index, e.target.value)}
                    placeholder="https://drive.google.com/..."
                    className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent pl-10"
                  />
                </div>
                {(formData.urlsProposition?.length || 0) > 1 && (
                  <button
                    type="button"
                    onClick={() => removeUrlField('urlsProposition', index)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => addUrlField('urlsProposition')}
              className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700"
            >
              <Plus className="w-4 h-4" />
              Ajouter une URL Proposition
            </button>
          </div>
        </FormField>

        {/* Accès workshop.qolaig.app par équipe */}
        <WorkshopTeamLinksField
          links={formData.teamLinks || []}
          visibleClient={formData.visibleClient ?? false}
          onLinksChange={(teamLinks) => setFormData((prev) => ({ ...prev, teamLinks }))}
          onVisibleClientChange={(visibleClient) => setFormData((prev) => ({ ...prev, visibleClient }))}
        />

        {/* Prix */}
        <FormField label="Prix (optionnel)">
          <Input
            type="number"
            value={formData.prix || ''}
            onChange={(e) => handleInputChange('prix', e.target.value)}
            placeholder="Prix du workshop"
            step="0.01"
            min="0"
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
          />
        </FormField>

        {/* Activity Sector Section */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Catégorie</label>
          
          {showNewSectorInput ? (
            <div className="space-y-4 border border-blue-200 rounded-lg p-4 bg-blue-50/30">
              <FormField label="Nom du nouveau secteur" required>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={formData.newActivitySectorName || ''}
                    onChange={(e) => handleInputChange('newActivitySectorName', e.target.value)}
                    placeholder="Ex: E-commerce, Fintech, Santé..."
                    className="bg-white border-gray-200 focus:ring-cyan-400 focus:border-transparent flex-1"
                  />
                  <Button
                    type="button"
                    onClick={handleCreateSector}
                    isLoading={creatingSector}
                    disabled={!formData.newActivitySectorName?.trim() || creatingSector}
                    className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
                  >
                    Créer
                  </Button>
                </div>
              </FormField>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewSectorInput(false)}
                className="text-sm"
              >
                Annuler
              </Button>
            </div>
          ) : (
            <SearchableSelect
              value={formData.activitySectorId || ''}
              onChange={(value) => handleInputChange('activitySectorId', value)}
              options={activitySectors.map(sector => ({
                value: String(sector.id),
                label: sector.nom
              }))}
              placeholder={loadingSectors ? "Chargement..." : "Sélectionner une catégorie"}
              searchPlaceholder="Rechercher une catégorie..."
              disabled={loadingSectors}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
              showAddButton={true}
              addButtonLabel="Ajouter un secteur"
              onAddClick={() => setShowNewSectorInput(true)}
            />
          )}
        </div>
      </div>
    </Modal>
  )
}
